import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/context/LanguageContext";
import {
  Archive, Database, Download, Clock, CheckCircle2, XCircle, AlertCircle,
  Play, HardDrive, TrendingDown, FileText, RefreshCw, Search, Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";
import { archivalApi } from "@/lib/api";
import { toast } from "sonner";

export default function DataArchival() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState("overview");
  const [runArchiveDialogOpen, setRunArchiveDialogOpen] = useState(false);
  const [requestDataDialogOpen, setRequestDataDialogOpen] = useState(false);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  // Data state
  const [stats, setStats] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [requests, setRequests] = useState([]);
  const [config, setConfig] = useState(null);

  // Form state
  const [archiveForm, setArchiveForm] = useState({ collection: "audit_logs", tenant: "all", retention_years: 3 });
  const [requestForm, setRequestForm] = useState({ data_type: "", date_from: "", date_to: "", output_format: "parquet", tenant: "all" });
  const [configForm, setConfigForm] = useState({ retention_years: 3, archive_frequency: "monthly", grace_period_days: 30, storage_location: "AWS S3 Glacier", encryption_enabled: true, compression_enabled: true });

  const [submittingArchive, setSubmittingArchive] = useState(false);
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [submittingConfig, setSubmittingConfig] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [statsData, jobsData, requestsData, configData] = await Promise.all([
        archivalApi.getStats(),
        archivalApi.listJobs(),
        archivalApi.listRequests(),
        archivalApi.getConfig(),
      ]);
      setStats(statsData);
      setJobs(jobsData);
      setRequests(requestsData);
      setConfig(configData);
      setConfigForm(configData);
    } catch {
      toast.error("Failed to load archival data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRunArchive = async () => {
    setSubmittingArchive(true);
    try {
      await archivalApi.createJob(archiveForm);
      toast.success("Archive job started in background");
      setRunArchiveDialogOpen(false);
      fetchAll();
    } catch (e) {
      toast.error(e.message || "Failed to start archive job");
    } finally {
      setSubmittingArchive(false);
    }
  };

  const handleRequestData = async () => {
    if (!requestForm.data_type || !requestForm.date_from || !requestForm.date_to) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmittingRequest(true);
    try {
      await archivalApi.createRequest(requestForm);
      toast.success("Retrieval request submitted successfully");
      setRequestDataDialogOpen(false);
      setRequestForm({ data_type: "", date_from: "", date_to: "", output_format: "parquet", tenant: "all" });
      fetchAll();
    } catch (e) {
      toast.error(e.message || "Failed to submit request");
    } finally {
      setSubmittingRequest(false);
    }
  };

  const handleSaveConfig = async () => {
    setSubmittingConfig(true);
    try {
      const updated = await archivalApi.updateConfig(configForm);
      setConfig(updated);
      toast.success("Configuration saved");
      setConfigDialogOpen(false);
    } catch (e) {
      toast.error(e.message || "Failed to save configuration");
    } finally {
      setSubmittingConfig(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed": case "ready":
        return <CheckCircle2 className="w-4 h-4 text-emerald-500" />;
      case "in_progress": case "processing":
        return <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />;
      case "failed":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "expired":
        return <AlertCircle className="w-4 h-4 text-amber-500" />;
      default:
        return <Clock className="w-4 h-4 text-slate-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      completed: "bg-emerald-50 text-emerald-700 border-emerald-200",
      ready: "bg-emerald-50 text-emerald-700 border-emerald-200",
      in_progress: "bg-blue-50 text-blue-700 border-blue-200",
      processing: "bg-blue-50 text-blue-700 border-blue-200",
      failed: "bg-red-50 text-red-700 border-red-200",
      expired: "bg-amber-50 text-amber-700 border-amber-200",
      scheduled: "bg-slate-50 text-slate-700 border-slate-200",
    };
    return variants[status] || variants.scheduled;
  };

  const totalActive = stats ? Object.values(stats.active_records).reduce((a, b) => a + b, 0) : 0;
  const totalArchived = stats ? stats.archived_records.total : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16 text-slate-400 gap-2">
        <Loader2 className="w-5 h-5 animate-spin" /> Loading archival data...
      </div>
    );
  }

  return (
    <div data-testid="data-archival-page">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-slate-900 tracking-tight" data-testid="data-archival-title">
          {t("archival.title")}
        </h1>
        <p className="text-sm text-slate-500 mt-1">{t("archival.subtitle")}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center">
              <Database className="w-5 h-5 text-blue-600" />
            </div>
            <Badge variant="outline" className="text-[10px] bg-blue-50 text-blue-700 border-blue-200">
              {config?.retention_years || 3} {t("archival.retention_years")}
            </Badge>
          </div>
          <div className="text-2xl font-bold text-slate-900">{totalActive.toLocaleString()}</div>
          <div className="text-xs text-slate-500">{t("archival.active_data")}</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center">
              <Archive className="w-5 h-5 text-purple-600" />
            </div>
            <span className="text-xs text-slate-400">Cold Storage</span>
          </div>
          <div className="text-2xl font-bold text-slate-900">{totalArchived.toLocaleString()}</div>
          <div className="text-xs text-slate-500">{t("archival.archived_data")}</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center">
              <TrendingDown className="w-5 h-5 text-emerald-600" />
            </div>
            <span className="text-xs text-emerald-600 font-medium">{stats?.completed_jobs || 0} jobs done</span>
          </div>
          <div className="text-2xl font-bold text-slate-900">{(stats?.archivable_records || 0).toLocaleString()}</div>
          <div className="text-xs text-slate-500">Eligible for archival</div>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="w-10 h-10 bg-amber-50 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-amber-600" />
            </div>
            {(stats?.pending_requests || 0) > 0 && (
              <Badge className="bg-amber-500 text-white text-[10px]">{stats.pending_requests} pending</Badge>
            )}
          </div>
          <div className="text-2xl font-bold text-slate-900">{stats?.ready_requests || 0}</div>
          <div className="text-xs text-slate-500">{t("archival.retrieval_requests")} Ready</div>
        </div>
      </div>

      {/* Tabs + Action Buttons */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="jobs">{t("archival.archive_jobs")}</TabsTrigger>
            <TabsTrigger value="requests">{t("archival.retrieval_requests")}</TabsTrigger>
            <TabsTrigger value="entities">Data Entities</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { setConfigForm(config || {}); setConfigDialogOpen(true); }}>
            <HardDrive className="w-4 h-4 mr-2" /> Configuration
          </Button>
          <Button variant="outline" onClick={() => setRequestDataDialogOpen(true)} data-testid="request-data-btn">
            <Download className="w-4 h-4 mr-2" /> {t("archival.request_data")}
          </Button>
          <Button onClick={() => setRunArchiveDialogOpen(true)} data-testid="run-archive-btn">
            <Play className="w-4 h-4 mr-2" /> {t("archival.run_archive")}
          </Button>
        </div>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-slate-200 rounded-lg">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-medium text-slate-900">Recent {t("archival.archive_jobs")}</h3>
              <Button variant="ghost" size="sm" onClick={() => setActiveTab("jobs")}>View All</Button>
            </div>
            <div className="divide-y divide-slate-100">
              {jobs.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-400">No archive jobs yet</div>
              ) : jobs.slice(0, 3).map(job => (
                <div key={job.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(job.status)}
                    <div>
                      <div className="text-sm font-medium text-slate-900 truncate max-w-[180px]">{job.name}</div>
                      <div className="text-xs text-slate-500">{(job.records_archived || 0).toLocaleString()} records</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className={`text-[10px] ${getStatusBadge(job.status)}`}>
                      {job.status.replace("_", " ")}
                    </Badge>
                    {job.status === "in_progress" && (
                      <div className="mt-1"><Progress value={job.progress || 0} className="h-1 w-20" /></div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-medium text-slate-900">Recent {t("archival.retrieval_requests")}</h3>
              <Button variant="ghost" size="sm" onClick={() => setActiveTab("requests")}>View All</Button>
            </div>
            <div className="divide-y divide-slate-100">
              {requests.length === 0 ? (
                <div className="px-4 py-8 text-center text-sm text-slate-400">No retrieval requests yet</div>
              ) : requests.slice(0, 3).map(req => (
                <div key={req.id} className="px-4 py-3 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {getStatusIcon(req.status)}
                    <div>
                      <div className="text-sm font-medium text-slate-900">{req.tenant}</div>
                      <div className="text-xs text-slate-500">{req.data_type} — {req.date_range}</div>
                    </div>
                  </div>
                  <div className="text-right">
                    <Badge variant="outline" className={`text-[10px] ${getStatusBadge(req.status)}`}>
                      {req.status}
                    </Badge>
                    {req.status === "ready" && (
                      <div className="text-xs text-slate-400 mt-1">{t("archival.expires_in")} {req.expires_in}</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {config && (
            <div className="bg-white border border-slate-200 rounded-lg lg:col-span-2">
              <div className="px-4 py-3 border-b border-slate-200">
                <h3 className="font-medium text-slate-900">{t("archival.retention_policy")} Configuration</h3>
              </div>
              <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-slate-500 mb-1">Retention Period</div>
                  <div className="text-lg font-semibold text-slate-900">{config.retention_years} Years</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Archive Frequency</div>
                  <div className="text-lg font-semibold text-slate-900 capitalize">{config.archive_frequency}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Grace Period</div>
                  <div className="text-lg font-semibold text-slate-900">{config.grace_period_days} Days</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500 mb-1">Storage Location</div>
                  <div className="text-lg font-semibold text-slate-900 text-sm">{config.storage_location}</div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Jobs Tab */}
      {activeTab === "jobs" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search archive jobs..." className="pl-9" readOnly />
            </div>
            <Button variant="ghost" size="sm" onClick={fetchAll}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
          {jobs.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">
              No archive jobs found. Click "Run Archive" to start one.
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-[1fr_120px_140px_120px_110px_160px] bg-slate-50 border-b border-slate-200">
                {["Job Name","Collection",t("label.status"),t("archival.records_archived"),t("archival.storage_saved"),"Started"].map((h,i) => (
                  <div key={i} className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
                ))}
              </div>
              {jobs.map(job => (
                <div key={job.id} className="grid grid-cols-[1fr_120px_140px_120px_110px_160px] border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <div className="px-4 py-3 flex items-start gap-2">
                    {getStatusIcon(job.status)}
                    <div>
                      <span className="text-sm font-medium text-slate-900">{job.name}</span>
                      {job.error && <div className="text-xs text-red-500 mt-1">{job.error}</div>}
                    </div>
                  </div>
                  <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{job.collection || job.tenant}</div>
                  <div className="px-4 py-3 flex items-center">
                    <div>
                      <Badge variant="outline" className={`text-[10px] ${getStatusBadge(job.status)}`}>{job.status.replace("_"," ")}</Badge>
                      {job.status === "in_progress" && (
                        <div className="mt-1 flex items-center gap-2">
                          <Progress value={job.progress || 0} className="h-1 w-16" />
                          <span className="text-xs text-slate-400">{job.progress || 0}%</span>
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{(job.records_archived || 0).toLocaleString()}</div>
                  <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{job.storage_saved || "0 MB"}</div>
                  <div className="px-4 py-3 text-sm text-slate-500 flex items-center">{job.started_at ? new Date(job.started_at).toLocaleString() : "—"}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Requests Tab */}
      {activeTab === "requests" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input placeholder="Search retrieval requests..." className="pl-9" readOnly />
            </div>
            <Button variant="ghost" size="sm" onClick={fetchAll}>
              <RefreshCw className="w-4 h-4 mr-1" /> Refresh
            </Button>
          </div>
          {requests.length === 0 ? (
            <div className="py-12 text-center text-sm text-slate-400">
              No retrieval requests yet. Click "Request Data" to restore archived records.
            </div>
          ) : (
            <div>
              <div className="grid grid-cols-[120px_120px_1fr_120px_80px_160px] bg-slate-50 border-b border-slate-200">
                {["Tenant",t("archival.data_type"),t("archival.date_range"),t("label.status"),"Format","Requested At"].map((h,i) => (
                  <div key={i} className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
                ))}
              </div>
              {requests.map(req => (
                <div key={req.id} className="grid grid-cols-[120px_120px_1fr_120px_80px_160px] border-b border-slate-100 hover:bg-slate-50 transition-colors">
                  <div className="px-4 py-3 text-sm font-medium text-slate-900 flex items-center">{req.tenant}</div>
                  <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{req.data_type}</div>
                  <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{req.date_range}</div>
                  <div className="px-4 py-3 flex items-center">
                    <Badge variant="outline" className={`text-[10px] ${getStatusBadge(req.status)}`}>{req.status}</Badge>
                  </div>
                  <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{req.format}</div>
                  <div className="px-4 py-3 text-sm text-slate-500 flex items-center">{req.requested_at ? new Date(req.requested_at).toLocaleString() : "—"}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Entities Tab */}
      {activeTab === "entities" && (
        <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-200">
            <p className="text-sm text-slate-500">Data classification showing which entities are subject to archival</p>
          </div>
          <div>
            <div className="grid grid-cols-[1fr_150px_150px_150px] bg-slate-50 border-b border-slate-200">
              {["Entity Name","Classification","Active Records","Archived Records"].map(h => (
                <div key={h} className="px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">{h}</div>
              ))}
            </div>
            {[
              { name: "audit_logs", type: "transactional", active: stats?.active_records?.audit_logs, archived: stats?.archived_records?.audit_logs },
              { name: "payment_transactions", type: "transactional", active: stats?.active_records?.payment_transactions, archived: stats?.archived_records?.payment_transactions },
              { name: "users", type: "master", active: null, archived: null },
              { name: "tenants", type: "master", active: null, archived: null },
              { name: "api_keys", type: "master", active: null, archived: null },
            ].map((entity, i) => (
              <div key={i} className="grid grid-cols-[1fr_150px_150px_150px] border-b border-slate-100 hover:bg-slate-50 transition-colors">
                <div className="px-4 py-3 flex items-center">
                  <code className="text-sm font-mono text-slate-900 bg-slate-100 px-1.5 py-0.5 rounded">{entity.name}</code>
                </div>
                <div className="px-4 py-3 flex items-center">
                  <Badge variant="outline" className={`text-[10px] ${entity.type === "master" ? "bg-purple-50 text-purple-700 border-purple-200" : "bg-blue-50 text-blue-700 border-blue-200"}`}>
                    {entity.type === "master" ? t("archival.master") : t("archival.transactional")}
                  </Badge>
                </div>
                <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{entity.active != null ? entity.active.toLocaleString() : "—"}</div>
                <div className="px-4 py-3 text-sm text-slate-600 flex items-center">{entity.archived != null ? entity.archived.toLocaleString() : "—"}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Run Archive Dialog */}
      <Dialog open={runArchiveDialogOpen} onOpenChange={setRunArchiveDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="run-archive-desc">
          <DialogHeader>
            <DialogTitle>{t("archival.run_archive")}</DialogTitle>
            <DialogDescription id="run-archive-desc">
              Move old transactional data to cold storage.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-xs">Collection</Label>
              <Select value={archiveForm.collection} onValueChange={(v) => setArchiveForm(f => ({ ...f, collection: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="audit_logs">Audit Logs</SelectItem>
                  <SelectItem value="billing_transactions">Billing Transactions</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Archive Data Older Than</Label>
              <Select value={String(archiveForm.retention_years)} onValueChange={(v) => setArchiveForm(f => ({ ...f, retention_years: parseInt(v) }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Year</SelectItem>
                  <SelectItem value="2">2 Years</SelectItem>
                  <SelectItem value="3">3 Years (Default)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
              <div className="text-xs text-amber-800">
                <strong>Note:</strong> This runs as a background job. Master data (users, tenants) will not be affected.
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRunArchiveDialogOpen(false)}>{t("action.cancel")}</Button>
            <Button onClick={handleRunArchive} disabled={submittingArchive} data-testid="confirm-run-archive-btn">
              {submittingArchive ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting...</> : t("archival.run_archive")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Request Data Dialog */}
      <Dialog open={requestDataDialogOpen} onOpenChange={setRequestDataDialogOpen}>
        <DialogContent className="max-w-md" aria-describedby="request-data-desc">
          <DialogHeader>
            <DialogTitle>{t("archival.request_data")}</DialogTitle>
            <DialogDescription id="request-data-desc">
              Request archived data for download. A secure link will be provided once ready.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-xs">{t("archival.data_type")}</Label>
              <Select value={requestForm.data_type} onValueChange={(v) => setRequestForm(f => ({ ...f, data_type: v }))}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="Select data type" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="api_logs">API Logs</SelectItem>
                  <SelectItem value="audit_logs">Audit Logs</SelectItem>
                  <SelectItem value="billing">Billing Records</SelectItem>
                  <SelectItem value="device_metrics">Device Metrics</SelectItem>
                  <SelectItem value="entropy_logs">Entropy Logs</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">{t("label.from")}</Label>
                <Input type="date" value={requestForm.date_from} onChange={(e) => setRequestForm(f => ({ ...f, date_from: e.target.value }))} className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">{t("label.to")}</Label>
                <Input type="date" value={requestForm.date_to} onChange={(e) => setRequestForm(f => ({ ...f, date_to: e.target.value }))} className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">{t("label.format")}</Label>
              <Select value={requestForm.output_format} onValueChange={(v) => setRequestForm(f => ({ ...f, output_format: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="parquet">Parquet (Recommended)</SelectItem>
                  <SelectItem value="csv">CSV</SelectItem>
                  <SelectItem value="json">JSON</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-800">
              <strong>Download Link Validity:</strong> 7 days from when the request is ready.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRequestDataDialogOpen(false)}>{t("action.cancel")}</Button>
            <Button onClick={handleRequestData} disabled={submittingRequest} data-testid="confirm-request-data-btn">
              {submittingRequest ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</> : `${t("action.submit")} Request`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Configuration Dialog */}
      <Dialog open={configDialogOpen} onOpenChange={setConfigDialogOpen}>
        <DialogContent className="max-w-lg" aria-describedby="config-desc">
          <DialogHeader>
            <DialogTitle>Archive Configuration</DialogTitle>
            <DialogDescription id="config-desc">Configure global archival settings and retention policies</DialogDescription>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-xs">Data Retention Period (Years)</Label>
              <Select value={String(configForm.retention_years || 3)} onValueChange={(v) => setConfigForm(f => ({ ...f, retention_years: parseInt(v) }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="1">1 Year</SelectItem>
                  <SelectItem value="2">2 Years</SelectItem>
                  <SelectItem value="3">3 Years (Default)</SelectItem>
                  <SelectItem value="5">5 Years</SelectItem>
                  <SelectItem value="7">7 Years</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Archive Frequency</Label>
              <Select value={configForm.archive_frequency || "monthly"} onValueChange={(v) => setConfigForm(f => ({ ...f, archive_frequency: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="weekly">Weekly</SelectItem>
                  <SelectItem value="monthly">Monthly (Recommended)</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Grace Period (Days before hard delete)</Label>
              <Input type="number" value={configForm.grace_period_days || 30} onChange={(e) => setConfigForm(f => ({ ...f, grace_period_days: parseInt(e.target.value) || 0 }))} className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Archive Storage</Label>
              <Select value={configForm.storage_location || "AWS S3 Glacier"} onValueChange={(v) => setConfigForm(f => ({ ...f, storage_location: v }))}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="AWS S3 Glacier">AWS S3 Glacier</SelectItem>
                  <SelectItem value="AWS S3 Infrequent Access">AWS S3 Infrequent Access</SelectItem>
                  <SelectItem value="Azure Blob Cool">Azure Blob Cool</SelectItem>
                  <SelectItem value="GCP Coldline">GCP Coldline</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between pt-2">
              <div>
                <Label className="text-sm">Encryption at Rest</Label>
                <p className="text-xs text-slate-500">AES-256 encryption for archived data</p>
              </div>
              <Switch checked={!!configForm.encryption_enabled} onCheckedChange={(v) => setConfigForm(f => ({ ...f, encryption_enabled: v }))} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <Label className="text-sm">Compression</Label>
                <p className="text-xs text-slate-500">Compress data before archiving</p>
              </div>
              <Switch checked={!!configForm.compression_enabled} onCheckedChange={(v) => setConfigForm(f => ({ ...f, compression_enabled: v }))} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigDialogOpen(false)}>{t("action.cancel")}</Button>
            <Button onClick={handleSaveConfig} disabled={submittingConfig} data-testid="save-config-btn">
              {submittingConfig ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : `${t("action.save")} Configuration`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
